import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgremoAdminService } from '../../../core/services/agremo-admin.service';
import { AgremoAnalysis } from '../../../core/models/agremo.model';

/**
 * Organization Analyses Tab Component
 *
 * Scoped read-only table of Agremo analyses for a single organization.
 * Same columns as the global analyses view minus the organization name.
 * No edit/delete actions.
 */
@Component({
  selector: 'app-organization-analyses-tab',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './organization-analyses-tab.component.html',
  styleUrls: ['./organization-analyses-tab.component.scss']
})
export class OrganizationAnalysesTabComponent implements OnInit {
  @Input() organizationId!: string;

  analyses: AgremoAnalysis[] = [];
  loading = true;
  errorMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalAnalyses = 0;
  totalPages = 0;

  constructor(
    private agremoService: AgremoAdminService
  ) {}

  ngOnInit(): void {
    if (!this.organizationId) {
      this.errorMessage = 'Organization ID is required';
      this.loading = false;
      return;
    }

    this.loadAnalyses();
  }

  loadAnalyses(): void {
    this.loading = true;
    this.errorMessage = '';

    this.agremoService.listOrganizationAnalyses(this.organizationId, {
      page: this.currentPage,
      limit: this.pageSize
    }).subscribe({
      next: (response) => {
        this.analyses = response.analyses;
        this.totalAnalyses = response.total;
        this.totalPages = Math.ceil(this.totalAnalyses / this.pageSize);
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load analyses:', error);
        this.errorMessage = 'Failed to load analyses. Please try again.';
        this.loading = false;
      }
    });
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadAnalyses();
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'processing': return 'bg-info';
      case 'queued': return 'bg-primary';
      case 'pending': return 'bg-warning text-dark';
      case 'failed': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'completed': return 'Completed';
      case 'processing': return 'Processing';
      case 'queued': return 'Queued';
      case 'pending': return 'Pending';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  get showingStart(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalAnalyses);
  }
}
